import { html, nothing } from 'lit';
import { ago } from '../../shared/ui/format.js';
import { inviteMember, withdrawInvite } from '../api/developer.js';
import { AccountView } from './account-view.js';

/**
 * The invitations still open: sent, not yet accepted, not withdrawn.
 *
 * An invitation counts as a seat from the moment it is sent, so withdrawing one gives the seat back -
 * which is the one action here. Sending one is the members page's job.
 */
export class UserAccountInvitations extends AccountView
{
   static section = 'members';

   static subpage = { id: 'invitations', label: 'Invitations' };

   static properties = { inviting: { state: true } };

   constructor()
   {
      super();
      this.inviting = false;
   }

   get pageActions()
   {
      return [{ id: 'invite', label: 'Invite somebody', icon: 'fa-solid fa-user-plus', variant: 'primary', disabled: this.busy }];
   }

   onPageAction(id)
   {
      if (id === 'invite')
      {
         this.inviting = true;
      }
   }

   invite(event)
   {
      event.preventDefault();

      const address = String(new FormData(event.target).get('email') ?? '').trim();

      if (!address)
      {
         return;
      }

      this.inviting = false;

      void this.act(
         () => inviteMember(this.account, address),
         invite => `Invited ${invite.email}. The link was mailed to them, and their seat counts from now.`);
   }

   get body()
   {
      const rows = this.team.invites ?? [];

      return html`
         ${this.inviting
            ? html`
               <lia-card title="Invite somebody" icon="fa-solid fa-user-plus">
                  <form class="d-flex flex-wrap gap-2" @submit=${this.invite}>
                     <input
                        class="form-control form-control-sm flex-grow-1 covdbg-address"
                        type="email"
                        name="email"
                        placeholder="colleague@example.com"
                        aria-label="Address to invite"
                        required
                        autofocus
                        ?disabled=${this.busy}
                     >
                     <button class="btn btn-sm btn-primary" type="submit" ?disabled=${this.busy}>Send invitation</button>
                     <button class="btn btn-sm btn-link" type="button" @click=${() => { this.inviting = false; }}>
                        Cancel
                     </button>
                  </form>
               </lia-card>`
            : nothing}

         <lia-card>
            <lia-data-table
               listing-id="account-invites"
               .perPage=${10}
               no-column-manager
               ?loading=${this.busy}
               .columns=${[
                  { key: 'email', label: 'Address', sortable: true, searchable: true },
                  { key: 'createdBy', label: 'Invited by', sortable: true, render: row => row.createdBy || '—' },
                  { key: 'createdAt', label: 'Sent', sortable: true, render: row => ago(row.createdAt) },
                  { key: 'expiresAt', label: 'Expires', sortable: true, render: row => expiryWord(row.expiresAt) }
               ]}
               .rows=${rows}
               .rowKey=${row => row.id}
               empty-title="None open"
               empty-message="Everybody invited has accepted."
               .rowActions=${row => [{
                  id: 'withdraw',
                  icon: 'fa-solid fa-ban',
                  title: 'Withdraw this invitation',
                  variant: 'outline-danger',
                  size: 'sm',
                  disabled: this.busy,
                  confirm: {
                     title: 'Withdraw this invitation?',
                     message: `The link sent to ${row.email} stops working and the seat is given back.`,
                     confirmLabel: 'Withdraw',
                     variant: 'danger'
                  }
               }]}
               @lia-action=${event =>
               {
                  const row = event.detail?.row;

                  if (row)
                  {
                     void this.act(() => withdrawInvite(this.account, row.id), `Withdrew the invitation to ${row.email}.`);
                  }
               }}
            ></lia-data-table>
         </lia-card>`;
   }
}

function expiryWord(value)
{
   const at = new Date(value).getTime();

   return Number.isNaN(at) ? '—' : at < Date.now() ? 'Expired' : ago(value);
}

customElements.define('user-account-invitations', UserAccountInvitations);
